import React, { useEffect, useState } from "react";
import { useHistory } from 'react-router-dom';
import ApiRest from "../../../service/ApiRest";
import { NaeTable } from "nae-react-core-styles";
import TableWithPaging from "../../TableWithPaging";
import { UI } from "@newageerp/nae-react-ui";

const moduleName = 'candidate';

interface PhoneNumber {
    id: number;
    country: string;
    number: string;
    type: string;
    comment?: string;
}

interface Address {
    id: number;
    country: string;
    city: string;
    streetAddress: string;
    postCode: string;
    comment?: string;
}

interface Candidate {
    id: number;
    fullName: string;
    birthDate: string;
    applicationDate: string;
    position: string;
    citizenship: string;
    status: 'review' | 'accepted' | 'rejected';
    email?: string;
    phoneNumbers?: PhoneNumber[];
    history: Array<{
        id: number;
        date: string;
        change: string;
    }>;
}


interface CandidateListState {
    search: string;
    activePage: number;
    selectedPosition: string;
    selectedStatus: string;
}

export default function CandidateListCard() {
    const history = useHistory<CandidateListState>();

    const [readyToLoad, setReadyToLoad] = useState(false);
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [dataToRender, setDataToRender] = useState<Candidate[]>([]);
    const [activePage, setActivePage] = useState(1);
    const [search, setSearch] = useState('');
    const [selectedPosition, setSelectedPosition] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>('review');

    const uniquePositions = React.useMemo(() => {
        const positions = candidates
            .map(candidate => candidate.position)
            .filter((position): position is string => !!position);
        return Array.from(new Set(positions)).sort();
    }, [candidates]);

    const getStatusLabel = (status: string) => {
        const statusMap: Record<string, string> = {
            'review': 'Vertinamas',
            'accepted': 'Priimtas',
            'rejected': 'Atmestas'
        };
        return statusMap[status] || status;
    };

    const getLatestComment = (candidate: Candidate) => {
        if (candidate.history && candidate.history.length > 0) {
            const sortedHistory = [...candidate.history].sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            return sortedHistory[0].change;
        }
        return '';
    };

    const getData = () => {
        ApiRest.getElements(moduleName).then(res => {
            setCandidates(res);
        });
    }

    useEffect(getData, []);

    const filterData = () => {
        if (readyToLoad) {
            let _data = candidates;
    
            if (search) {
                _data = _data.filter((item: Candidate) => {
                    const searchLower = search.toLowerCase();
                    return (
                        item.fullName.toLowerCase().includes(searchLower) ||
                        (item.email?.toLowerCase().includes(searchLower) ?? false) ||
                        (item.phoneNumbers?.some(phone => 
                            phone.number.toLowerCase().includes(searchLower)
                        ) ?? false)
                    );
                });
            }
    
            if (selectedPosition) {
                _data = _data.filter((item: Candidate) => item.position === selectedPosition);
            }
    
            if (selectedStatus) {
                _data = _data.filter((item: Candidate) => item.status === selectedStatus);
            }
    
            setDataToRender(_data);
        }
    }

    useEffect(filterData, [search, activePage, candidates, selectedPosition, selectedStatus, readyToLoad]);

    const restoreHistoryState = () => {
        const historyState = history.location.state as CandidateListState;

        if (historyState) {
            setSearch(historyState.search || '');
            setActivePage(historyState.activePage || 1);
            setSelectedPosition(historyState.selectedPosition || '');
            setSelectedStatus(historyState.selectedStatus || 'review');
        }

        setTimeout(() => {
            setReadyToLoad(true);
        }, 300);
    }

    useEffect(restoreHistoryState, []);

    useEffect(() => {
        if (readyToLoad) {
            setActivePage(1);
        }
    }, [search, selectedPosition, selectedStatus]);

    const goTo = (item: Candidate) => {
        const state: CandidateListState = {
            search,
            activePage,
            selectedPosition,
            selectedStatus
        };

        history.replace('/candidates', state);
        history.push('/' + moduleName + '/' + item.id);
    }

    const goToNew = () => {
        history.push('/' + moduleName + '/new');
    }

    return (
        <UI.Card.WhiteCard>
            <div className="flex items-center gap-2">
                <UI.Typography.H3 className="flex-grow">
                    Kandidatai
                </UI.Typography.H3>
                <UI.Buttons.Button icon="fal fa-fw fa-plus" onClick={goToNew}>
                    Naujas
                </UI.Buttons.Button>
            </div>

            <div className="flex items-center gap-2 mb-4">
                <UI.Form.Input
                    placeholder="Paieška"
                    className="flex-grow"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                <UI.Form.Select
                    className="w-48"
                    value={selectedPosition}
                    onChange={e => setSelectedPosition(e)}
                    options={[
                        { value: "", label: "Visos pareigos" },
                        ...uniquePositions.map(position => ({
                            value: position,
                            label: position
                        }))
                    ]}
                />

                <UI.Form.Select
                    className="w-48"
                    value={selectedStatus}
                    onChange={e => setSelectedStatus(e)}
                    options={[
                        { value: "", label: "Visi statusai" },
                        { value: "review", label: "Vertinamas" },
                        { value: "accepted", label: "Priimtas" },
                        { value: "rejected", label: "Atmestas" }
                    ]}
                />
            </div>

            <TableWithPaging
                activePage={activePage}
                setActivePage={setActivePage}
                dataToRender={dataToRender}
                head={
                    <tr>
                        <NaeTable.th>Vardas Pavardė</NaeTable.th>
                        <NaeTable.th>Gimimo data</NaeTable.th>
                        <NaeTable.th>Paraiškos data</NaeTable.th>
                        <NaeTable.th>Pareigos</NaeTable.th>
                        <NaeTable.th>Pilietybė</NaeTable.th>
                        <NaeTable.th>Statusas</NaeTable.th>
                    </tr>
                }
                renderItem={(item: Candidate) => {
                    return (
                        <tr onClick={() => goTo(item)} key={"list-row-" + item.id}>
                            <NaeTable.td link={true}>{item.fullName}</NaeTable.td>
                            <NaeTable.td>{item.birthDate ? new Date(item.birthDate).toLocaleDateString('lt-LT') : ''}</NaeTable.td>
                            <NaeTable.td>{item.applicationDate ? new Date(item.applicationDate).toLocaleDateString('lt-LT') : ''}</NaeTable.td>
                            <NaeTable.td>{item.position}</NaeTable.td>
                            <NaeTable.td>{item.citizenship}</NaeTable.td>
                            <NaeTable.td>{getStatusLabel(item.status)}</NaeTable.td>
                        </tr>
                    )
                }}
            />
        </UI.Card.WhiteCard>
    );
}